import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/** What the planner is told. Mirrors PlanTerritoriesRequest. */
export interface TerritoryPlanChoice {
  territoryCount: number | null;
  balanceBy: 'leads' | 'value';
  assignReps: boolean;
}

/** What the page knows that changes the answer, so the dialog can say it. */
export interface TerritoryPlanFacts {
  /** Reps this plan could hand patches to. The default count follows it. */
  reps: number;
  /** Territories a person drew. The planner covers the ground they leave. */
  manual: number;
  /** Territories a previous plan drew. A new run replaces these. */
  planned: number;
}

/**
 * WHAT TO ASK BEFORE REDRAWING SOMEBODY'S MAP.
 *
 * It ran on one click and no questions: one patch per rep, balanced by
 * pipeline value, reps handed out round-robin. Those are good defaults and
 * they are still the defaults - but they were also the only answers, and a
 * territory map is the thing a sales manager argues about most. Being unable
 * to say "six, by door count, and I will assign them myself" meant redrawing
 * by hand afterwards, which is the work the planner exists to remove.
 *
 * THREE QUESTIONS, because three is what the planner can actually honour.
 * Anything else offered here would be a control that changes nothing, which is
 * worse than a missing one: it teaches people the plan listens when it does
 * not.
 *
 * The dialog also says what the run will DO to what is already there - a plan
 * replaces the patches a previous plan drew - because that is the part nobody
 * can undo, and it was not written anywhere before.
 */
@Component({
  selector: 'app-territory-plan-dialog',
  templateUrl: './territory-plan-dialog.component.html',
  styleUrls: ['./territory-plan-dialog.component.css'],
})
export class TerritoryPlanDialogComponent {
  /** Null follows the roster. Held apart from the number so that clearing the
   *  box reads as "one each" rather than as zero territories. */
  followRoster = true;
  count: number | null = null;
  balanceBy: 'leads' | 'value' = 'value';

  /**
   * Always on, and no longer asked.
   *
   * It was a question, and the answer people wanted was neither yes nor no:
   * they wanted THIS territory to go to THAT rep, which a single switch
   * cannot say. Round-robin is a starting point rather than a decision - the
   * roster list assigns a rep per row, in one click, so the plan hands them
   * out and you correct whichever ones are wrong.
   *
   * Kept as a field because the server still takes it, and a caller that
   * genuinely wants unowned territories is a request away.
   */
  readonly assignReps = true;

  /** The planner's own ceiling. Asking for more is not refused politely
   *  somewhere out of sight - it is clamped, so the box says so instead. */
  static readonly MaxTerritories = 60;

  constructor(
    private readonly ref: MatDialogRef<TerritoryPlanDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly facts: TerritoryPlanFacts,
  ) {
    this.count = facts.reps > 0 ? facts.reps : 6;
  }

  get effectiveCount(): number {
    return this.followRoster
      ? (this.facts.reps > 0 ? this.facts.reps : 6)
      : Math.min(
        Math.max(this.count ?? 1, 1),
        TerritoryPlanDialogComponent.MaxTerritories);
  }

  /** True when a number was typed that the planner would silently change. */
  get countClamped(): boolean {
    return !this.followRoster && this.count !== null
      && (this.count < 1 || this.count > TerritoryPlanDialogComponent.MaxTerritories);
  }

  cancel(): void { this.ref.close(); }

  run(): void {
    this.ref.close({
      // Null rather than the number it would be: the roster can change
      // between pressing this and the job running, and "one each" should
      // still mean one each when it does.
      territoryCount: this.followRoster ? null : this.effectiveCount,
      balanceBy: this.balanceBy,
      assignReps: this.assignReps,
    } satisfies TerritoryPlanChoice);
  }
}
